// src/pages/Appli/AppliAdmin/PrestationsAdmin/CreatePrestationModal.jsx
import React, { useMemo, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import {
  createPrestation,
  uploadImagePrestation,
} from "./../../../../services/prestationsServices";
import { useNotification } from "./../../../../components/Notification/NotificationManager";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3317";

const normLang = (l) => {
  const v = String(l || "fr").toLowerCase();
  if (v.startsWith("fr")) return "fr";
  if (v.startsWith("en")) return "en";
  return "fr";
};

const isExternalURL = (url) => {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
};

const toAbs = (url) => {
  if (!url) return "";
  return isExternalURL(url) ? url : `${BASE_URL}${url}`;
};

const CreatePrestationModal = ({ onClose, onCreate, language }) => {
  const { addNotification } = useNotification();
  const lang = normLang(language);

  const ui = useMemo(() => {
    const card =
      "rounded-3xl border border-black/10 bg-white/95 shadow-xl " +
      "dark:bg-slate-900 dark:border-white/10";
    const title = "text-gray-900 dark:text-white";
    const muted = "text-gray-700 dark:text-gray-300";
    const input =
      "w-full rounded-2xl px-3 py-2 text-sm outline-none transition " +
      "border border-black/10 dark:border-white/10 " +
      "bg-white dark:bg-black/20 " +
      "text-gray-900 dark:text-white " +
      "focus:ring-2 focus:ring-primary focus:border-transparent";
    const btnPrimary =
      "inline-flex items-center justify-center rounded-2xl px-4 py-2 text-sm font-bold " +
      "bg-primary text-white hover:opacity-95 active:scale-[0.99] transition";
    const btnGhost =
      "inline-flex items-center justify-center rounded-2xl px-4 py-2 text-sm font-bold " +
      "border border-black/10 dark:border-white/10 " +
      "bg-white/70 dark:bg-black/20 " +
      "text-gray-900 dark:text-white " +
      "hover:bg-white dark:hover:bg-black/30 transition";
    const chip =
      "inline-flex items-center rounded-full px-3 py-1 text-xs font-bold " +
      "border border-black/10 dark:border-white/10 " +
      "bg-white/70 dark:bg-black/20 text-gray-900 dark:text-white";
    return { card, title, muted, input, btnPrimary, btnGhost, chip };
  }, []);

  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [prestation_image, setPrestationImage] = useState("");
  const [prestation_ordre, setPrestationOrdre] = useState("");
  const [loading, setLoading] = useState(false);

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const fd = new FormData();
    fd.append("image", file); // attendu côté backend

    setLoading(true);
    try {
      const uploadResponse = await uploadImagePrestation(fd);
      if (uploadResponse?.filePath) {
        setPrestationImage(uploadResponse.filePath);
        addNotification("Image uploadée avec succès !", "success");
      } else {
        throw new Error("Réponse invalide de l'API");
      }
    } catch (e) {
      console.error(e);
      addNotification("Erreur lors de l'upload de l'image.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    const ordreNum =
      prestation_ordre !== "" && prestation_ordre !== null && prestation_ordre !== undefined
        ? Number(prestation_ordre)
        : undefined;

    // ✅ crée uniquement dans la langue courante (l’autre pourra être complétée ensuite via l’edit)
    const payload = {
      prestation_title_fr: lang === "fr" ? title : "",
      prestation_content_fr: lang === "fr" ? content : "",
      prestation_title_en: lang === "en" ? title : "",
      prestation_content_en: lang === "en" ? content : "",
      prestation_image,
      prestation_ordre: ordreNum,
    };

    Object.keys(payload).forEach((k) => payload[k] === undefined && delete payload[k]);

    try {
      setLoading(true);
      const created = await createPrestation(payload);
      if (!created) throw new Error("Échec de création");

      addNotification("Prestation créée avec succès.", "success");
      await onCreate?.(created);
      onClose?.();
    } catch (e) {
      console.error(e?.response?.data || e);
      addNotification(
        `Erreur création prestation: ${e?.response?.data?.error || "Voir console"}`,
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const previewUrl = toAbs(prestation_image);

  return (
    <div
      className="fixed inset-0 z-[999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div className={`${ui.card} w-full max-w-3xl overflow-hidden`} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="px-6 py-4 border-b border-black/10 dark:border-white/10 flex items-center justify-between gap-3">
          <div>
            <h2 className={`text-xl sm:text-2xl font-extrabold ${ui.title}`}>
              Créer une prestation
            </h2>
            <p className={`text-sm ${ui.muted} mt-1`}>
              Remplis le contenu en <span className="font-extrabold">{lang.toUpperCase()}</span>.
            </p>
          </div>
          <span className={ui.chip}>{lang.toUpperCase()}</span>
        </div>

        {/* Body */}
        <div className="p-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Image */}
          <div className="lg:col-span-5">
            <div className="rounded-3xl border border-black/10 dark:border-white/10 bg-white/60 dark:bg-black/20 overflow-hidden">
              <div className="aspect-[16/10] flex items-center justify-center">
                {previewUrl ? (
                  <img src={previewUrl} alt="Prévisualisation" className="h-full w-full object-contain" />
                ) : (
                  <div className={`p-8 text-sm text-center ${ui.muted}`}>Aucune image</div>
                )}
              </div>
            </div>

            <div className="mt-3">
              <label className={`text-xs font-semibold ${ui.muted} block mb-1`}>
                Image
              </label>
              <input type="file" accept="image/*" onChange={handleImageUpload} />
              {prestation_image && (
                <p className={`text-xs mt-2 ${ui.muted}`}>Chemin : {prestation_image}</p>
              )}
            </div>

            <div className="mt-3">
              <label className={`text-xs font-semibold ${ui.muted} block mb-1`}>
                Ordre
              </label>
              <input
                type="number"
                className={ui.input}
                value={prestation_ordre}
                onChange={(e) => setPrestationOrdre(e.target.value)}
                placeholder="1, 2, 3…"
              />
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-7">
            <div className="mb-4">
              <label className={`text-xs font-semibold ${ui.muted} block mb-1`}>
                Titre ({lang.toUpperCase()})
              </label>
              <input
                className={ui.input}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Titre…"
              />
            </div>

            <div className="mb-4">
              <label className={`text-xs font-semibold ${ui.muted} block mb-1`}>
                Contenu ({lang.toUpperCase()})
              </label>
              <div className="rounded-3xl border border-black/10 dark:border-white/10 overflow-hidden bg-white dark:bg-black/20">
                <ReactQuill
                  theme="snow"
                  value={content}
                  onChange={setContent}
                  className="prestationsadmin-quill"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-black/10 dark:border-white/10 flex items-center justify-end gap-2">
          <button type="button" className={ui.btnGhost} onClick={onClose} disabled={loading}>
            Annuler
          </button>
          <button type="button" className={ui.btnPrimary} onClick={handleSubmit} disabled={loading}>
            {loading ? "Enregistrement…" : "Créer"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreatePrestationModal;
